import { Cancel } from "@mui/icons-material";
import {
  Card,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import ImagePicker from "components/ApnaUploader";
import AstrieskIcon from "components/AstrieskIcon";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { handleAlert } from "redux/festures/alertSlice";
import { creatCategoryData, updateCategory } from "redux/festures/categorySlice";
import { fetchAllCities } from "redux/festures/cityCRUD";
import http from "Utils/api2";

const initialForm = {
  name: "",
  price: "",
  description: "",
  workExperience: "",
  status: "active",
  disable: "false",
  cityId: [],
  pCategory: "",
};

const allowedImageTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const allowedVideoTypes = ["video/mp4", "video/quicktime", "video/webm", "video/x-matroska"];
const maxElements = 3;

const getMediaUrl = (path) => `${process.env.REACT_APP_URI}${path || ""}`;

const extractImageBanners = (banner = []) => banner.filter((item) => item?.type === "IMAGE");
const extractVideoBanners = (banner = []) => banner.filter((item) => item?.type === "VIDEO");

export default function ServiceSubCategoryForm({
  parentId,
  parentName,
  viewData,
  isUpdate,
  onClose,
  onSuccess,
}) {
  const dispatch = useDispatch();
  const adminId = localStorage.getItem("admin_id");
   const { id: routeParentId } = useParams();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { createUpdateLoading } = useSelector((state) => state.isCategory);
  const { cities } = useSelector((state) => state.isCityManager);

  const [pCategories, setPCategories] = useState([]);
  const [pCategoryLoading, setPCategoryLoading] = useState(false);
  const [formData, setFormData] = useState(initialForm);
  const [iconFile, setIconFile] = useState(null);
  const [bannerFiles, setBannerFiles] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [videoFiles, setVideoFiles] = useState([]);
  const [formErrors, setFormErrors] = useState({});

  const existingBannerImages = useMemo(
    () => extractImageBanners(viewData?.banner || []),
    [viewData]
  );
  const existingBannerVideos = useMemo(
    () => extractVideoBanners(viewData?.banner || []),
    [viewData]
  );
  const existingImages = useMemo(() => viewData?.images || [], [viewData]);
  const existingVideos = useMemo(() => viewData?.videos || [], [viewData]);

  const selectedParentCategory = useMemo(
    () => pCategories.find((item) => item?._id === formData.pCategory),
    [formData.pCategory, pCategories]
  );

  useEffect(() => {
    if (!adminId) return;

    dispatch(fetchAllCities(adminId));

    const loadParentCategories = async () => {
      setPCategoryLoading(true);
      try {
        const response = await http.get(`/getAllNullPcategory/${adminId}?disable=false`);
        if (response?.data?.success) {
          setPCategories(response.data.data || []);
        }
      } catch (error) {
        setPCategories([]);
      } finally {
        setPCategoryLoading(false);
      }
    };

    loadParentCategories();
  }, [adminId, dispatch]);

  useEffect(() => {
    if (viewData && isUpdate) {
      setFormData({
        name: viewData?.name || "",
        price:
          viewData?.price !== undefined && viewData?.price !== null ? String(viewData.price) : "",
        description: viewData?.description || "",
        workExperience:
          viewData?.workExperience !== undefined && viewData?.workExperience !== null
            ? String(viewData.workExperience)
            : "",
        status: viewData?.status || "active",
        disable: String(Boolean(viewData?.disable)),
        cityId: Array.isArray(viewData?.cityId)
          ? viewData.cityId
              .map((item) => (typeof item === "string" ? item : item?._id))
              .filter(Boolean)
          : [],
        pCategory: viewData?.pCategory?._id || viewData?.pCategory || parentId || "",
      });
    } else {
      setFormData({
        ...initialForm,
        pCategory: parentId || "",
      });
    }

    setIconFile(null);
    setBannerFiles([]);
    setImageFiles([]);
    setVideoFiles([]);
    setFormErrors({});
  }, [viewData, isUpdate, parentId]);

  const validateField = (name, value) => {
    switch (name) {
      case "name":
        if (!value?.trim()) return "Sub service name is required";
        if (!/^[A-Za-z ]+$/.test(value.trim())) return "Only alphabets and spaces are allowed";
        if (value.trim().length < 2) return "Name should be at least 2 characters";
        return "";
      case "price":
        if (value === "" || value === null) return "Price is required";
        if (Number(value) <= 0) return "Price must be greater than 0";
        return "";
      case "description":
        if (!value?.trim()) return "Description is required";
        if (value.trim().length < 3) return "Description should be at least 3 characters";
        return "";
      case "workExperience":
        if (value === "" || value === null) return "Work experience is required";
        if (Number(value) < 0) return "Work experience cannot be negative";
        return "";
      case "status":
        if (!value) return "Status is required";
        return "";
      case "disable":
        if (!["true", "false"].includes(String(value))) return "Disable value is invalid";
        return "";
      case "cityId":
        if (!Array.isArray(value) || value.length === 0) return "At least one city is required";
        if (value.length > 100) return "Maximum 100 cities are allowed";
        return "";
      case "pCategory":
        if (!value) return "Parent category is required";
        return "";
      default:
        return "";
    }
  };

  const validateFiles = () => {
    const errors = {};

    if (!iconFile && !viewData?.icon) {
      errors.icon = "Icon image is required";
    } else if (iconFile && !allowedImageTypes.includes(iconFile.type)) {
      errors.icon = "Icon must be jpg, jpeg, png, or webp";
    }

    if (!bannerFiles.length && !existingBannerImages.length) {
      errors.banner = "At least one banner image is required";
    } else if (bannerFiles.length > maxElements) {
      errors.banner = "Maximum 3 banner images are allowed";
    } else if (bannerFiles.find((file) => !allowedImageTypes.includes(file.type))) {
      errors.banner = "All banner files must be jpg, jpeg, png, or webp";
    }

    if (imageFiles.length > maxElements) {
      errors.images = "Maximum 3 gallery images are allowed";
    } else if (imageFiles.find((file) => !allowedImageTypes.includes(file.type))) {
      errors.images = "All gallery images must be jpg, jpeg, png, or webp";
    }

    if (videoFiles.length > maxElements) {
      errors.videos = "Maximum 3 videos are allowed";
    } else if (videoFiles.find((file) => !allowedVideoTypes.includes(file.type))) {
      errors.videos = "Videos must be mp4, mov, webm, or mkv";
    }

    return errors;
  };

  const validateForm = () => {
    const nextErrors = {};

    Object.entries(formData).forEach(([key, value]) => {
      const error = validateField(key, value);
      if (error) nextErrors[key] = error;
    });

    const fileErrors = validateFiles();
    const allErrors = { ...nextErrors, ...fileErrors };
    setFormErrors(allErrors);
    return Object.keys(allErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "name" && !/^[A-Za-z ]*$/.test(value)) return;
    if (["price", "workExperience"].includes(name) && value !== "" && !/^\d*\.?\d*$/.test(value)) {
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: validateField(name, value),
      }));
    }
  };

  const handleMultiFiles = (
    setter,
    errorKey,
    typeList,
    files,
    invalidTypeMessage,
    maxCountMessage
  ) => {
    const selectedFiles = Array.isArray(files) ? files : [];

    if (selectedFiles.length > maxElements) {
      setter(selectedFiles.slice(0, maxElements));
      setFormErrors((prev) => ({ ...prev, [errorKey]: maxCountMessage }));
      return;
    }

    setter(selectedFiles);
    setFormErrors((prev) => ({
      ...prev,
      [errorKey]: selectedFiles.find((file) => !typeList.includes(file.type))
        ? invalidTypeMessage
        : "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!adminId) {
      dispatch(
        handleAlert({
          isOpen: true,
          type: "error",
          msg: "Admin id is missing. Please login again.",
        })
      );
      return;
    }

    if (!validateForm()) {
      dispatch(
        handleAlert({
          isOpen: true,
          type: "warning",
          msg: "Please fix all validation errors before submit",
        })
      );
      return;
    }

    const payload = new FormData();
    payload.append("name", formData.name.trim());
    payload.append("price", formData.price);
    payload.append("description", formData.description.trim());
    payload.append("workExperience", formData.workExperience);
    payload.append("status", formData.status);
    payload.append("disable", formData.disable);
    payload.append("pCategory", formData.pCategory);

    if (iconFile) payload.append("icon", iconFile);
    formData.cityId.forEach((cityId) => payload.append("cityId", cityId));
    bannerFiles.forEach((file) => payload.append("banner", file));
    imageFiles.forEach((file) => payload.append("images", file));
    videoFiles.forEach((file) => payload.append("videos", file));

    const response = await dispatch(
      isUpdate
        ? updateCategory({
            url: `/updateCategory/${viewData?._id}/${adminId}`,
            data: payload,
          })
        : creatCategoryData({
            url: `/createCategory/${adminId}`,
            reqBody: payload,
          })
    );

    dispatch(
      handleAlert({
        isOpen: true,
        type: response?.payload?.success ? "success" : "error",
        msg:
          response?.payload?.message ||
          (isUpdate ? "Sub category updated successfully" : "Sub category created successfully"),
      })
    );

    if (response?.payload?.success) {
      if (typeof onSuccess === "function") onSuccess();
      if (typeof onClose === "function") onClose();
    }
  };

  return (
    <MDBox
      display="flex"
      alignItems="center"
      lineHeight={1}
      sx={({ palette: { dark, white, info } }) => ({
        border: 0.5,
        borderColor: darkMode ? white.main : dark.main,
        borderRadius: 3,
        p: 2,
        width: "100%",
        //   height: "70vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: 2,
      })}
    >
      <Card
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 , 10px",
        }}
      >
        <MDTypography variant="h6" color="white">
          {isUpdate ? "Update Sub Category" : "Create Sub Category"}
        </MDTypography>
        <MDTypography variant="button" color="white" fontWeight="regular">
          {parentName || selectedParentCategory?.name || "Service Sub Category"}
        </MDTypography>
      </Card>

      <MDBox component="form" onSubmit={handleSubmit} p={3}>
        <Grid container spacing={2}>
          {/* <Grid item xs={12} md={6}>
            <MDTypography variant="h6">
              Parent Category <AstrieskIcon />
            </MDTypography>
            <TextField
              select
              fullWidth
              size="small"
              name="pCategory"
              value={formData.pCategory}
              onChange={handleInputChange}
              disabled={pCategoryLoading || createUpdateLoading}
              error={Boolean(formErrors.pCategory)}
              helperText={
                formErrors.pCategory ||
                (selectedParentCategory
                  ? `Selected ID: ${selectedParentCategory._id}`
                  : "Select by category name")
              }
              sx={{ mt: 1 }}
            >
              <MenuItem value="">Select Parent Category</MenuItem>
              {pCategories.map((category) => (
                <MenuItem key={category?._id} value={category?._id}>
                  {`${category?.name || "Unnamed"} (${category?._id})`}
                </MenuItem>
              ))}
            </TextField>
          </Grid> */}

          <Grid item xs={12} md={6}>
            <MDTypography variant="h6">
              Sub Service Name <AstrieskIcon />
            </MDTypography>
            <MDInput
              fullWidth
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter sub service name"
              error={Boolean(formErrors.name)}
              sx={{ mt: 1 }}
              disabled={createUpdateLoading}
            />
            {formErrors.name ? (
              <MDTypography variant="caption" color="error">
                {formErrors.name}
              </MDTypography>
            ) : null}
          </Grid>

          <Grid item xs={12} md={6}>
            <MDTypography variant="h6">
              Price <AstrieskIcon />
            </MDTypography>
            <MDInput
              fullWidth
              name="price"
              value={formData.price}
              onChange={handleInputChange}
              placeholder="Enter price"
              type="text"
              error={Boolean(formErrors.price)}
              sx={{ mt: 1 }}
              disabled={createUpdateLoading}
            />
            {formErrors.price ? (
              <MDTypography variant="caption" color="error">
                {formErrors.price}
              </MDTypography>
            ) : null}
          </Grid>

          <Grid item xs={12} md={6}>
            <MDTypography variant="h6">
              Work Experience (Years) <AstrieskIcon />
            </MDTypography>
            <MDInput
              fullWidth
              name="workExperience"
              value={formData.workExperience}
              onChange={handleInputChange}
              placeholder="Enter work experience"
              type="text"
              error={Boolean(formErrors.workExperience)}
              sx={{ mt: 1 }}
              disabled={createUpdateLoading}
            />
            {formErrors.workExperience ? (
              <MDTypography variant="caption" color="error">
                {formErrors.workExperience}
              </MDTypography>
            ) : null}
          </Grid>

          <Grid item xs={12}>
            <MDTypography variant="h6">
              Description <AstrieskIcon />
            </MDTypography>
            <MDInput
              fullWidth
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Write service description"
              multiline
              rows={4}
              error={Boolean(formErrors.description)}
              sx={{ mt: 1 }}
              disabled={createUpdateLoading}
            />
            {formErrors.description ? (
              <MDTypography variant="caption" color="error">
                {formErrors.description}
              </MDTypography>
            ) : null}
          </Grid>

          {/* <Grid item xs={12} md={6}>
            <MDTypography variant="h6">
              Status <AstrieskIcon />
            </MDTypography>
            <TextField
              select
              fullWidth
              size="small"
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              error={Boolean(formErrors.status)}
              helperText={formErrors.status}
              disabled={createUpdateLoading}
              sx={{ mt: 1 }}
            >
              <MenuItem value="active">active</MenuItem>
              <MenuItem value="inactive">inactive</MenuItem>
            </TextField>
          </Grid> */}

          {/* <Grid item xs={12} md={6}>
            <MDTypography variant="h6">
              Disable <AstrieskIcon />
            </MDTypography>
            <TextField
              select
              fullWidth
              size="small"
              name="disable"
              value={formData.disable}
              onChange={handleInputChange}
              error={Boolean(formErrors.disable)}
              helperText={formErrors.disable}
              disabled={createUpdateLoading}
              sx={{ mt: 1 }}
            >
              <MenuItem value="false">false</MenuItem>
              <MenuItem value="true">true</MenuItem>
            </TextField>
          </Grid> */}

          <Grid item xs={12}>
            <MDTypography variant="h6">
              City Id <AstrieskIcon />
            </MDTypography>
            <FormControl fullWidth size="small" error={Boolean(formErrors.cityId)} sx={{ mt: 1 }}>
              <Select
                multiple
                sx={{height: "40px"}}
                name="cityId"
                value={formData.cityId}
                onChange={handleInputChange}
                displayEmpty
                disabled={createUpdateLoading}
                renderValue={(selected) => {
                  if (!selected?.length) return "Select cities (min 1)";
                  return selected
                    .map((id) => cities?.find((city) => city?._id === id)?.cityName || id)
                    .join(", ");
                }}
              >
                <MenuItem disabled value="">
                  Select cities
                </MenuItem>
                {cities?.length ? (
                  cities.map((city) => (
                    <MenuItem key={city._id} value={city._id}>
                      {city.cityName}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No cities available</MenuItem>
                )}
              </Select>
              {formErrors.cityId ? <FormHelperText>{formErrors.cityId}</FormHelperText> : null}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <MDTypography variant="h6">
              Icon Image <AstrieskIcon />
              <MDTypography variant="body1" component="span" fontSize={11}>
                &nbsp; ( image size - 178 × 178 px )
              </MDTypography>
            </MDTypography>
            <MDBox mt={1}>
              <ImagePicker
                name="sub-service-icon"
                images={iconFile}
                setImages={setIconFile}
                multiple={false}
                accept="image/*"
                disabled={createUpdateLoading}
              />
            </MDBox>
            {!iconFile && viewData?.icon ? (
              <MDBox mt={1} display="flex" alignItems="center" gap={1}>
                <img
                  src={getMediaUrl(viewData.icon)}
                  alt="existing icon"
                  style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 8 }}
                />
                <Cancel sx={{ color: "text.secondary" }} />
              </MDBox>
            ) : null}
            {formErrors.icon ? (
              <MDTypography variant="caption" color="error">
                {formErrors.icon}
              </MDTypography>
            ) : null}
          </Grid>

          <Grid item xs={12} md={6}>
            <MDTypography variant="h6">
              Banner Image <AstrieskIcon />
              <MDTypography variant="body1" component="span" fontSize={11}>
                &nbsp; ( image size - 178 × 178 px )
              </MDTypography>
            </MDTypography>
            <MDBox mt={1}>
              <ImagePicker
                name="sub-service-banner"
                images={bannerFiles}
                setImages={(files) =>
                  handleMultiFiles(
                    setBannerFiles,
                    "banner",
                    allowedImageTypes,
                    files,
                    "All banner files must be jpg, jpeg, png, or webp",
                    "Maximum 3 banner images are allowed"
                  )
                }
                multiple
                accept="image/*"
                disabled={createUpdateLoading}
              />
            </MDBox>
            {!bannerFiles.length && existingBannerImages.length ? (
              <MDBox mt={1} display="flex" flexWrap="wrap" gap={1}>
                {existingBannerImages.map((item, index) => (
                  <img
                    key={`${item?.url}-${index}`}
                    src={getMediaUrl(item?.url)}
                    alt="existing banner"
                    style={{ width: 88, height: 72, objectFit: "cover", borderRadius: 8 }}
                  />
                ))}
              </MDBox>
            ) : null}
            {formErrors.banner ? (
              <MDTypography variant="caption" color="error">
                {formErrors.banner}
              </MDTypography>
            ) : null}
          </Grid>

          <Grid item xs={12} md={6}>
            <MDTypography variant="h6">
              Images (Optional){" "}
              <MDTypography variant="body1" component="span" fontSize={11}>
                &nbsp; ( image size - 540 × 320 px )
              </MDTypography>
            </MDTypography>
            <MDBox mt={1}>
              <ImagePicker
                name="sub-service-images"
                images={imageFiles}
                setImages={(files) =>
                  handleMultiFiles(
                    setImageFiles,
                    "images",
                    allowedImageTypes,
                    files,
                    "All gallery images must be jpg, jpeg, png, or webp",
                    "Maximum 3 gallery images are allowed"
                  )
                }
                multiple
                accept="image/*"
                disabled={createUpdateLoading}
              />
            </MDBox>
            {!imageFiles.length && existingImages.length ? (
              <MDBox mt={1} display="flex" flexWrap="wrap" gap={1}>
                {existingImages.map((item, index) => (
                  <img
                    key={`${item?.url || item}-${index}`}
                    src={getMediaUrl(item?.url || item)}
                    alt="existing gallery"
                    style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 8 }}
                  />
                ))}
              </MDBox>
            ) : null}
            {formErrors.images ? (
              <MDTypography variant="caption" color="error">
                {formErrors.images}
              </MDTypography>
            ) : null}
          </Grid>

          <Grid item xs={12} md={6}>
            <MDTypography variant="h6">Videos (Optional)</MDTypography>
            <MDBox mt={1}>
              <input
                type="file"
                multiple
                accept="video/*"
                disabled={createUpdateLoading}
                onChange={(e) =>
                  handleMultiFiles(
                    setVideoFiles,
                    "videos",
                    allowedVideoTypes,
                    Array.from(e.target.files || []),
                    "Videos must be mp4, mov, webm, or mkv",
                    "Maximum 3 videos are allowed"
                  )
                }
                style={{ width: "100%" }}
              />
            </MDBox>
            {!videoFiles.length && (existingVideos.length || existingBannerVideos.length) ? (
              <MDTypography variant="caption" mt={1} display="block">
                Existing videos: {existingVideos.length + existingBannerVideos.length}
              </MDTypography>
            ) : null}
            {videoFiles.length ? (
              <MDTypography variant="caption" mt={1} display="block">
                {videoFiles.length} file(s) selected
              </MDTypography>
            ) : null}
            {formErrors.videos ? (
              <MDTypography variant="caption" color="error">
                {formErrors.videos}
              </MDTypography>
            ) : null}
          </Grid>
        </Grid>

        <MDBox
          mt={3}
          sx={{
            display: "flex",
            justifyContent: { xs: "stretch", md: "flex-end" },
            gap: 1.5,
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <MDButton
            variant="outlined"
            color="secondary"
            disabled={createUpdateLoading}
            onClick={onClose}
          >
            Cancel
          </MDButton>
          <MDButton type="submit" color="info" variant="gradient" disabled={createUpdateLoading}>
            {createUpdateLoading ? (
              <CircularProgress color="inherit" size={20} />
            ) : isUpdate ? (
              "Update Sub Category"
            ) : (
              "Create Sub Category"
            )}
          </MDButton>
        </MDBox>
      </MDBox>
    </MDBox>
  );
}

ServiceSubCategoryForm.propTypes = {
  parentId: PropTypes.string,
  parentName: PropTypes.string,
  viewData: PropTypes.any,
  isUpdate: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
};
